import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useWizardStore } from '../wizardStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

export function MembersStep() {
  const { next, name, subscriptionId } = useWizardStore();
  const t = useT();
  const [copied, setCopied] = useState(false);

  const patchMutation = useMutation({
    mutationFn: () => {
      if (!subscriptionId) { next(); return Promise.resolve(null); }
      return subscriptionApi.patch(subscriptionId, { draft_step: 'collect' });
    },
    onSuccess: (result) => { if (result !== null) next(); },
  });

  const handleCopy = () => {
    // Real invite link is generated after creation; we copy a placeholder for UX flow
    void navigator.clipboard.writeText(`t.me/eestec_split_bot?start=invite_preview`).catch(() => null);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-4 flex-1">
      <h1 className="text-[24px] font-bold tracking-tight">{t('wizard.members.title')}</h1>

      {/* Holder badge */}
      <div className="flex items-center gap-3 rounded-[14px] px-4 py-3 glass-content">
        <span className="w-[40px] h-[40px] rounded-full bg-brand-gradient flex items-center justify-center text-white text-[18px] font-bold flex-shrink-0">
          {name.trim() ? name.trim()[0].toUpperCase() : t('wizard.members.holderFallback')}
        </span>
        <div className="min-w-0">
          <p className="text-[15px] font-semibold truncate">{name || '…'}</p>
          <p className="text-[12px] text-status-credit font-medium">{t('wizard.members.youHolder')}</p>
        </div>
      </div>

      {/* Invite link card */}
      <div className="rounded-[16px] p-4 bg-white/40 border-2 border-dashed border-brand-green/50">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[14px] font-bold">{t('wizard.members.inviteTitle')}</p>
            <p className="text-[12px] text-text-muted mt-0.5 font-mono truncate">
              t.me/eestec_split_bot?start=…
            </p>
            <p className="text-[11px] text-text-muted mt-1">{t('wizard.members.inviteAfterCreate')}</p>
          </div>
          <button
            onClick={handleCopy}
            className={cn(
              'flex-shrink-0 text-[13px] font-semibold px-3 py-[7px] rounded-[9px] transition-all',
              copied
                ? 'bg-status-paid/10 text-status-paid'
                : 'text-white bg-brand-gradient shadow-[0_4px_12px_rgba(18,120,90,0.3)]',
            )}
          >
            {copied ? t('wizard.members.copied') : t('wizard.members.copy')}
          </button>
        </div>

        {/* Instruction */}
        <div className="mt-3 pt-3 border-t border-black/[0.07] flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0c9981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <p className="text-[13px] text-text-secondary">{t('wizard.members.inviteNote')}</p>
        </div>
      </div>

      <div className="flex-1" />

      <WizardFooter>
        {patchMutation.isError && (
          <p className="text-[13px] text-status-debt text-center">{t('wizard.preview.error')}</p>
        )}
        <PrimaryButton onClick={() => patchMutation.mutate()} loading={patchMutation.isPending}>
          {t('wizard.members.allHere')}
        </PrimaryButton>
      </WizardFooter>
    </div>
  );
}
